import {useCallback, useEffect, useRef, useState, type RefObject} from 'react';

const PROGRAMMATIC_SCROLL_MS = 700;

const prefersReducedMotion = (): boolean =>
    typeof window !== 'undefined' && Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);

export const scrollRowIntoView = (container: HTMLElement | null, index: number, block: ScrollLogicalPosition = 'center'): void => {
    const row = container?.querySelector<HTMLElement>(`[data-segment-index="${index}"]`);
    row?.scrollIntoView?.({block, behavior: prefersReducedMotion() ? 'auto' : 'smooth'});
};

export const useFollowPlayback = (containerRef: RefObject<HTMLElement>, activeIndex: number, playing: boolean) => {
    const [following, setFollowing] = useState(true);
    const programmaticUntil = useRef(0);

    const scrollTo = useCallback((index: number, block: ScrollLogicalPosition = 'center') => {
        if (index < 0) return;
        programmaticUntil.current = Date.now() + PROGRAMMATIC_SCROLL_MS;
        scrollRowIntoView(containerRef.current, index, block);
    }, [containerRef]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const onUserScroll = () => setFollowing(false);
        const onScroll = () => {
            if (Date.now() > programmaticUntil.current) setFollowing(false);
        };
        container.addEventListener('wheel', onUserScroll, {passive: true});
        container.addEventListener('touchmove', onUserScroll, {passive: true});
        container.addEventListener('scroll', onScroll, {passive: true});
        return () => {
            container.removeEventListener('wheel', onUserScroll);
            container.removeEventListener('touchmove', onUserScroll);
            container.removeEventListener('scroll', onScroll);
        };
    }, [containerRef]);

    useEffect(() => {
        if (following && playing && activeIndex >= 0) scrollTo(activeIndex);
    }, [activeIndex, following, playing, scrollTo]);

    const backToCurrent = useCallback(() => {
        setFollowing(true);
        scrollTo(activeIndex);
    }, [activeIndex, scrollTo]);

    const stopFollowing = useCallback(() => setFollowing(false), []);

    return {following, backToCurrent, stopFollowing, scrollTo};
};
